/**
 * @module AdapterList
 * @package @geenius/adapters/react-css
 * @description Renders the searchable vanilla CSS adapter grid for the React
 * CSS variant. The list consumes provider status from context and delegates
 * selection events to the hosting page.
 */

import type { AdapterDomain, AdapterStatusType } from "@geenius/adapters";
import { ADAPTER_DOMAINS, DOMAIN_LABELS } from "@geenius/adapters";
import type { JSX } from "react";
import { useMemo, useState } from "react";
import { useAdapters } from "../hooks";
import { cx } from "../lib/cx";
import { AdapterCard } from "./AdapterCard";

/**
 * Props for rendering the React CSS adapter list and optional status filter controls.
 *
 * @property onSelect Optional callback invoked when a domain card is selected.
 * @property onConfigure Optional callback invoked from the empty-state CTA when no adapters are configured.
 * @property filterStatus Optional status filter applied before rendering the grid.
 * @property className Optional class name merged into the outer wrapper.
 */
export interface AdapterListProps {
  onSelect?: (domain: AdapterDomain) => void;
  onConfigure?: () => void;
  filterStatus?: AdapterStatusType;
  className?: string;
}

/**
 * Renders the searchable React CSS adapter list backed by shared provider state.
 *
 * @param props List props including selection callback, status filtering, and wrapper class names.
 * @returns The adapter grid or an empty/loading state when no entries are available.
 */
export function AdapterList({
  onSelect,
  onConfigure,
  filterStatus,
  className = "",
}: AdapterListProps): JSX.Element {
  const { statuses, isLoading } = useAdapters();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    let domains = [...ADAPTER_DOMAINS];
    const query = search.toLowerCase();

    if (query) {
      domains = domains.filter((domain) => {
        const label = (DOMAIN_LABELS[domain] ?? domain).toLowerCase();
        const provider = statuses[domain]?.provider?.toLowerCase() ?? "";
        return (
          label.includes(query) ||
          provider.includes(query) ||
          domain.includes(query)
        );
      });
    }

    if (filterStatus) {
      domains = domains.filter(
        (domain) => statuses[domain]?.status === filterStatus,
      );
    }

    return domains;
  }, [search, filterStatus, statuses]);

  if (isLoading) {
    return (
      <div
        className={cx("gn-adapters-grid", className)}
        role="status"
        aria-label="Loading adapters"
      >
        {[1, 2, 3, 4].map((index) => (
          <div
            key={index}
            className="gn-adapters-skeleton gn-adapters-skeleton--card"
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cx(className)}>
      <input
        type="text"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search adapters..."
        className="gn-adapters-search"
        aria-label="Search adapters"
      />
      {filtered.length === 0 ? (
        // Designed empty state with a recovery action: clear the search if one
        // is set, otherwise present a configuration CTA (when onConfigure is
        // provided). Plain text without an action fails UI_PROTOCOL §2.
        <div className="gn-adapters-empty">
          <p className="gn-adapters-empty__text">
            {search
              ? "No adapters match your search"
              : "No adapters configured"}
          </p>
          {search ? (
            <button
              type="button"
              className="gn-adapters-empty__action"
              onClick={() => setSearch("")}
            >
              Clear search
            </button>
          ) : onConfigure ? (
            <button
              type="button"
              className="gn-adapters-empty__action"
              onClick={() => onConfigure()}
            >
              Configure your first adapter
            </button>
          ) : null}
        </div>
      ) : (
        <ul className="gn-adapters-grid" aria-label="Adapter list">
          {filtered.map((domain) => (
            <li key={domain}>
              <AdapterCard
                domain={domain}
                status={statuses[domain]}
                onClick={onSelect ? () => onSelect(domain) : undefined}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
